/**
 * Sanitizes pathname to prevent XSS and injection attacks
 * Removes dangerous characters and limits length
 */
export const sanitizePathname = (pathname: string): string => {
  if (typeof pathname !== "string") {
    return "/";
  }
  // Remove potentially dangerous characters
  let sanitized = pathname.replace(/[<>"']/g, "");
  // Limit length to prevent DoS
  const MAX_PATHNAME_LENGTH = 2048;
  if (sanitized.length > MAX_PATHNAME_LENGTH) {
    sanitized = sanitized.substring(0, MAX_PATHNAME_LENGTH);
  }
  return sanitized;
};

/**
 * Sanitizes page title to prevent XSS
 * Removes HTML tags and limits length
 * Uses safe regex patterns to prevent ReDoS attacks
 */
export const sanitizeTitle = (title: string): string => {
  if (typeof title !== "string") {
    return "";
  }

  const MAX_TITLE_LENGTH = 300;

  // Limit input length first to prevent ReDoS attacks
  let sanitized =
    title.length > MAX_TITLE_LENGTH
      ? title.substring(0, MAX_TITLE_LENGTH)
      : title;

  // Remove HTML tags. Using [^<>] ensures we don't match across nested brackets,
  // preventing super-linear runtime (ReDoS) on strings with many '<' characters.
  sanitized = sanitized.replace(/<[^<>]*>/g, "");

  // Remove potentially dangerous characters
  sanitized = sanitized.replace(/[<>"']/g, "");

  return sanitized.trim();
};

/**
 * Validates email format before hashing
 * More robust validation without external libraries:
 * - Validates local part (before @) and domain part
 * - Prevents consecutive dots
 * - Ensures proper TLD format (at least 2 characters)
 * - Prevents leading/trailing dots
 */
export const isValidEmail = (email: string): boolean => {
  if (typeof email !== "string" || !email.trim()) {
    return false;
  }

  const trimmedEmail = email.trim();

  // Basic length check
  if (trimmedEmail.length > 254) {
    return false;
  }

  // Split into local and domain parts
  const parts = trimmedEmail.split("@");
  if (parts.length !== 2) {
    return false;
  }

  const [localPart, domainPart] = parts;

  // Validate local part (before @)
  if (!localPart || localPart.length === 0 || localPart.length > 64) {
    return false;
  }

  // Local part: alphanumeric, dots, hyphens, underscores, plus signs
  // Cannot start or end with dot
  if (
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..")
  ) {
    return false;
  }

  const localPartRegex = /^[a-zA-Z0-9._+-]+$/;
  if (!localPartRegex.test(localPart)) {
    return false;
  }

  // Validate domain part (after @)
  if (!domainPart || domainPart.length === 0) {
    return false;
  }

  // Domain cannot start or end with dot or hyphen
  if (
    domainPart.startsWith(".") ||
    domainPart.endsWith(".") ||
    domainPart.startsWith("-") ||
    domainPart.endsWith("-") ||
    domainPart.includes("..")
  ) {
    return false;
  }

  // Domain should have at least one dot (for TLD)
  if (!domainPart.includes(".")) {
    return false;
  }

  // Split domain into parts
  const domainParts = domainPart.split(".");
  const tld = domainParts[domainParts.length - 1];

  // TLD must be at least 2 characters and only letters
  if (!tld || tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
    return false;
  }

  // Each domain part should be valid (alphanumeric and hyphens, but not starting/ending with hyphen)
  for (const part of domainParts) {
    if (
      part.length === 0 ||
      part.startsWith("-") ||
      part.endsWith("-") ||
      !/^[a-zA-Z0-9-]+$/.test(part)
    ) {
      return false;
    }
  }

  return true;
};
